import type { ApiResponse, SignedRequest } from '@shared/types';
import { apiClient } from './client';

export type MessageArchivePolicyMode = 'disabled' | 'text' | 'text_with_attachments';

export type RedeemServerAdminClaimResponse = {
  serverAdmin: {
    serverAdminId: string;
    identityId: string;
    grantedAt: string;
  };
};

export type ServerAdminStatusResponse = {
  isServerAdmin: boolean;
  serverAdmin: {
    serverAdminId: string;
    identityId: string;
    grantedAt: string;
  } | null;
};

export type ServerAdminListItem = {
  serverAdminId: string;
  identityId: string;
  grantedAt: string;
  grantedVia: 'bootstrap' | 'recovery' | 'admin_grant';
};

export type ServerAdminTenantListItem = {
  familyId: string;
  host: string;
  publicBaseUrl: string;
  serverName: string;
  status: 'pending_owner' | 'active' | 'revoked';
  ownerIdentityId: string | null;
  createdAt: string;
  claimedAt: string | null;
  revokedAt: string | null;
  lastActivityAt: string | null;
  noNamesOnServer: boolean;
  ownerClaimExpiresAt: string | null;
  joinInviteExpiresAt: string | null;
  pendingOwnerClaimCount: number;
  identityCount: number;
  joinInviteToken: string | null;
};

export type CreateTenantResponse = {
  familyId: string;
  host: string;
  publicBaseUrl: string;
  serverName: string;
  tenantStatus: 'pending_owner';
  joinInviteToken: string;
  joinInviteExpiresAt: string;
  ownerClaimToken: string;
  ownerClaimExpiresAt: string;
};

export type ReissueOwnerClaimResponse = {
  familyId: string;
  joinInviteToken?: string;
  joinInviteExpiresAt?: string;
  ownerClaimToken: string;
  ownerClaimExpiresAt: string;
};

export type RevokeTenantResponse = {
  familyId: string;
  tenantStatus: 'revoked';
};

export type DeleteTenantResponse = {
  familyId: string;
  deleted: true;
};

export type TenantConfigResponse = {
  familyId: string;
  serverName: string;
  messageTtlHours: number;
  attachmentsEnabled: boolean;
  maxAttachmentFileSizeBytes: number | null;
  attachmentStorageQuotaBytes: number | null;
  attachmentRetentionSeconds: number | null;
  messageArchiveServerPolicy: MessageArchivePolicyMode;
  messageArchiveServerMaxBytes: number | null;
  messageArchiveCirclePolicy: MessageArchivePolicyMode;
  messageArchiveCircleMaxBytes: number | null;
};

export type UpdateTenantResponse = {
  familyId: string;
  serverName: string;
  messageTtlHours: number;
  attachmentsEnabled: boolean;
  maxAttachmentFileSizeBytes: number | null;
  attachmentStorageQuotaBytes: number | null;
  attachmentRetentionSeconds: number | null;
  messageArchiveServerPolicy: MessageArchivePolicyMode;
  messageArchiveServerMaxBytes: number | null;
  messageArchiveCirclePolicy: MessageArchivePolicyMode;
  messageArchiveCircleMaxBytes: number | null;
};

class ServerAdminAPI {
  async getStatus(
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<ServerAdminStatusResponse>> {
    return apiClient.signedRequest('/server-admin/status', signedPayload, serverUrl);
  }

  async redeemClaim(
    signedPayload: SignedRequest<{ claimToken: string }>,
    serverUrl?: string
  ): Promise<ApiResponse<RedeemServerAdminClaimResponse>> {
    return apiClient.signedRequest('/server-admin/claims/redeem', signedPayload, serverUrl);
  }

  async createTenant(
    signedPayload: SignedRequest<{
      serverName: string;
      ownerMode: 'self' | 'other';
      noNamesOnServer?: boolean;
      publicBaseUrl: string;
      joinInviteToken?: string | null;
      /** @deprecated Use joinInviteToken. */
      firstOwnerInviteToken?: string | null;
      ownerClaimTtlHours?: number;
      joinInviteTtlHours?: number;
      messageTtlHours?: number;
      attachmentsEnabled?: boolean;
      maxAttachmentFileSizeBytes?: number | null;
      attachmentStorageQuotaBytes?: number | null;
      attachmentRetentionSeconds?: number | null;
      messageArchiveServerPolicy?: MessageArchivePolicyMode;
      messageArchiveServerMaxBytes?: number | null;
      messageArchiveCirclePolicy?: MessageArchivePolicyMode;
      messageArchiveCircleMaxBytes?: number | null;
    }>,
    serverUrl?: string
  ): Promise<ApiResponse<CreateTenantResponse>> {
    return apiClient.signedRequest('/server-admin/tenants', signedPayload, serverUrl);
  }

  async reissueOwnerClaim(
    familyId: string,
    signedPayload: SignedRequest<{ ownerClaimTtlHours?: number }>,
    serverUrl?: string
  ): Promise<ApiResponse<ReissueOwnerClaimResponse>> {
    return apiClient.signedRequest(`/server-admin/tenants/${encodeURIComponent(familyId)}/reissue-owner-claim`, signedPayload, serverUrl);
  }

  async listTenants(
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ tenants: ServerAdminTenantListItem[] }>> {
    return apiClient.signedRequest('/server-admin/tenants/list', signedPayload, serverUrl);
  }

  async listAdmins(
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ admins: ServerAdminListItem[] }>> {
    return apiClient.signedRequest('/server-admin/admins/list', signedPayload, serverUrl);
  }

  async revokeTenant(
    familyId: string,
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<RevokeTenantResponse>> {
    return apiClient.signedRequest(`/server-admin/tenants/${encodeURIComponent(familyId)}/revoke`, signedPayload, serverUrl);
  }

  async deleteTenant(
    familyId: string,
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<DeleteTenantResponse>> {
    return apiClient.signedRequest(`/server-admin/tenants/${encodeURIComponent(familyId)}/delete`, signedPayload, serverUrl);
  }

  async getTenantConfig(
    familyId: string,
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<TenantConfigResponse>> {
    return apiClient.signedRequest(`/server-admin/tenants/${encodeURIComponent(familyId)}/config`, signedPayload, serverUrl);
  }

  async updateTenant(
    familyId: string,
    signedPayload: SignedRequest<{
      serverName?: string;
      messageTtlHours?: number;
      attachmentsEnabled?: boolean;
      maxAttachmentFileSizeBytes?: number | null;
      attachmentStorageQuotaBytes?: number | null;
      attachmentRetentionSeconds?: number | null;
      messageArchiveServerPolicy?: MessageArchivePolicyMode;
      messageArchiveServerMaxBytes?: number | null;
      messageArchiveCirclePolicy?: MessageArchivePolicyMode;
      messageArchiveCircleMaxBytes?: number | null;
    }>,
    serverUrl?: string
  ): Promise<ApiResponse<UpdateTenantResponse>> {
    return apiClient.signedRequest(`/server-admin/tenants/${encodeURIComponent(familyId)}/update`, signedPayload, serverUrl);
  }
}

export const serverAdminAPI = new ServerAdminAPI();
